/**
 * Remediation plans.
 *
 * Cards rather than a table. A plan carries a name, an owner, dates, a status,
 * a priority and a set of gaps — squeezing that into table columns produces
 * something unreadable on a phone and barely readable on a desktop.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import {
  Card, CardHeader, Button, Pill, Modal, EmptyState, Skeleton, StatTile,
  ErrorNotice, TONE_FOR_STATUS,
} from '../components/ui';
import { BarChart, type Datum } from '../components/charts';
import { IconPlan } from '../components/icons';
import { EvidencePanel } from '../components/EvidencePanel';
import { PLAN_STATUSES, TERMINAL_PLAN_STATUSES, type PlanStatus } from '@opencomply/shared';

interface Plan {
  id: string; name: string; status: string; priority: string;
  startDate: string | null; dueDate: string | null;
  issueDescription: string | null; mitigationActions: string | null;
  inherentRisk: string | null; residualRisk: string | null; inBudget: boolean;
  owner: { id: string; firstName: string; lastName: string } | null;
  gaps: Array<{ gap: { id: string; title: string; severity: string } }>;
}

export function PlansPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['plans', { search, status }],
    queryFn: () => api.get<{ items: Plan[]; total: number }>(`/plans${qs({ search, status, pageSize: 100 })}`),
  });

  const { data: kpis } = useQuery({
    queryKey: ['plans', 'kpis'],
    queryFn: () => api.get<{
      total: number; open: number; completed: number; atRisk: number;
      overdue: number; highCritical: number;
      byStatus: Record<string, number>; byPriority: Record<string, number>;
    }>('/plans/kpis'),
    enabled: allows('plan.read'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/plans/${id}`, patch),
    onSuccess: () => {
      setDetail(null);
      setError(null);
      void queryClient.invalidateQueries();
    },
    onError: (err) => setError(describeError(err)),
  });

  const formatDate = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso)) : '—';

  const items = data?.items ?? [];

  const statusData: Datum[] = PLAN_STATUSES.map((s) => ({
    key: s,
    label: t(`status.${s}`),
    value: kpis?.byStatus[s] ?? 0,
    tone: (TONE_FOR_STATUS[s] ?? 'neutral') as Datum['tone'],
  }));

  const isLocked = (plan: Plan) => TERMINAL_PLAN_STATUSES.includes(plan.status as PlanStatus);

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('plans.title')}</h1>
        <p className="oc-muted mt-1">{t('plans.subtitle')}</p>
      </header>

      {error && <ErrorNotice message={error} />}

      {kpis && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatTile label={t('common.total')} value={kpis.total} />
            <StatTile label={t('dashboard.openPlans')} value={kpis.open} />
            <StatTile label={t('status.AT_RISK')} value={kpis.atRisk} tone={kpis.atRisk > 0 ? 'serious' : undefined} />
            <StatTile label={t('plans.overdue')} value={kpis.overdue} tone={kpis.overdue > 0 ? 'critical' : undefined} />
          </div>
          <Card>
            <CardHeader title={t('dashboard.plansByStatus')} />
            <BarChart data={statusData} emptyLabel={t('plans.none')} />
          </Card>
        </>
      )}

      <Card className="!p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="oc-input sm:max-w-xs"
            placeholder={t('plans.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
          />
          <select className="oc-input sm:!w-auto" value={status} onChange={(e) => setStatus(e.target.value)}
                  aria-label={t('common.filter')}>
            <option value="">{t('common.all')}</option>
            {PLAN_STATUSES.map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
          </select>
        </div>
      </Card>

      {isLoading ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : items.length === 0 ? (
        <Card><EmptyState icon={<IconPlan className="w-10 h-10" />} title={t('plans.none')} /></Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {items.map((plan) => {
            const overdue =
              plan.dueDate && new Date(plan.dueDate) < new Date() && !isLocked(plan);
            return (
              <li key={plan.id}>
                <button
                  onClick={() => { setError(null); setDetail(plan); }}
                  className="oc-card p-4 w-full text-left hover:shadow-raised transition-shadow"
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="font-medium text-ink leading-snug min-w-0">{plan.name}</p>
                    <Pill tone={TONE_FOR_STATUS[plan.status] ?? 'neutral'} className="shrink-0">
                      {t(`status.${plan.status}`)}
                    </Pill>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Pill tone={TONE_FOR_STATUS[plan.priority] ?? 'neutral'}>
                      {t(`status.${plan.priority}`)}
                    </Pill>
                    <span className="oc-pill-neutral">
                      {plan.gaps.length} {t('plans.linkedGaps').toLowerCase()}
                    </span>
                    {overdue && <Pill tone="critical">{t('plans.overdue')}</Pill>}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div>
                      <dt className="text-ink-muted">{t('plans.owner')}</dt>
                      <dd className="text-ink-secondary truncate">
                        {plan.owner ? `${plan.owner.firstName} ${plan.owner.lastName}` : t('plans.unassigned')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-ink-muted">{t('plans.dueDate')}</dt>
                      <dd className={overdue ? 'text-critical font-medium' : 'text-ink-secondary'}>
                        {formatDate(plan.dueDate)}
                      </dd>
                    </div>
                  </dl>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Detail sheet */}
      <Modal
        open={detail !== null}
        onClose={() => setDetail(null)}
        title={detail?.name ?? ''}
        footer={
          detail && allows('plan.updateOwn') ? (
            isLocked(detail) ? (
              <Button
                variant="primary"
                onClick={() => update.mutate({ id: detail.id, patch: { reopen: true } })}
                disabled={update.isPending}
              >
                {t('plans.reopen')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => update.mutate({ id: detail.id, patch: { status: 'CANCELLED' } })}
                  disabled={update.isPending}
                >
                  {t('plans.cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => update.mutate({ id: detail.id, patch: { status: 'DONE' } })}
                  disabled={update.isPending}
                >
                  {t('plans.markDone')}
                </Button>
              </>
            )
          ) : null
        }
      >
        {detail && (
          <div className="space-y-4">
            {error && <ErrorNotice message={error} />}

            {isLocked(detail) && (
              <div className="rounded-xl bg-surface-sunken px-3 py-2.5 text-sm text-ink-secondary">
                {t('plans.locked', { status: t(`status.${detail.status}`) })}
              </div>
            )}

            <div className="flex flex-wrap gap-1.5">
              <Pill tone={TONE_FOR_STATUS[detail.status] ?? 'neutral'}>{t(`status.${detail.status}`)}</Pill>
              <Pill tone={TONE_FOR_STATUS[detail.priority] ?? 'neutral'}>{t(`status.${detail.priority}`)}</Pill>
              {detail.inBudget && <span className="oc-pill-neutral">{t('plans.inBudget')}</span>}
            </div>

            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-ink-muted text-xs">{t('plans.owner')}</dt>
                <dd className="text-ink">
                  {detail.owner ? `${detail.owner.firstName} ${detail.owner.lastName}` : t('plans.unassigned')}
                </dd>
              </div>
              <div>
                <dt className="text-ink-muted text-xs">{t('plans.dueDate')}</dt>
                <dd className="text-ink">{formatDate(detail.dueDate)}</dd>
              </div>
              {detail.inherentRisk && (
                <div>
                  <dt className="text-ink-muted text-xs">{t('plans.inherentRisk')}</dt>
                  <dd><Pill tone={TONE_FOR_STATUS[detail.inherentRisk] ?? 'neutral'}>{t(`status.${detail.inherentRisk}`)}</Pill></dd>
                </div>
              )}
              {detail.residualRisk && (
                <div>
                  <dt className="text-ink-muted text-xs">{t('plans.residualRisk')}</dt>
                  <dd><Pill tone={TONE_FOR_STATUS[detail.residualRisk] ?? 'neutral'}>{t(`status.${detail.residualRisk}`)}</Pill></dd>
                </div>
              )}
            </dl>

            {detail.mitigationActions && (
              <div>
                <p className="text-xs text-ink-muted mb-1">{t('plans.mitigation')}</p>
                <p className="text-sm text-ink-secondary leading-relaxed">{detail.mitigationActions}</p>
              </div>
            )}

            <div>
              <p className="text-xs text-ink-muted mb-2">
                {t('plans.linkedGaps')} ({detail.gaps.length})
              </p>
              <ul className="space-y-1.5">
                {detail.gaps.map(({ gap }) => (
                  <li key={gap.id} className="rounded-lg bg-surface-sunken px-3 py-2">
                    <p className="text-sm text-ink leading-snug">{gap.title}</p>
                    <Pill tone={TONE_FOR_STATUS[gap.severity] ?? 'neutral'} className="mt-1.5">
                      {t(`status.${gap.severity}`)}
                    </Pill>
                  </li>
                ))}
              </ul>
            </div>

            {/* Evidence stays editable on a plan in any status, including DONE
                and CANCELLED: evidence is often exactly what justifies closing
                a plan, and the server allows it. So readOnly is false here;
                the panel still hides upload/remove from anyone without the
                evidence capabilities. */}
            <div className="pt-4 border-t border-line">
              <EvidencePanel key={detail.id} planId={detail.id} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
